import { Button } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";
import { RpaButtonLink } from "../../../components/RpiLink";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { createDrawerOverlay, createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { RpaDrawer, RpaDrawerOverlay } from "../../../rpi-core/drawer/RpaDrawer";
import { dashboardUserRoleOptions } from "../../../utils/graphql.util";
import { validateEmail } from "../../../utils/validation.util";

const FORM_ID = "dashboard-user-create-form";

export const DashboardUserCreateView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    return (
      <RpaDrawer title="Add New User" opened={opened} onClose={handleClose}>
        <CreateUser onSuccess={() => handleClose(true)} onCancel={() => handleClose()} />
      </RpaDrawer>
    );
  }
);

interface CreateUserProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const CreateUser: React.FC<CreateUserProps> = ({ onSuccess, onCancel }) => {
  const [customersSearch, setCustomersSearch] = React.useState("");
  const [debouncedCustomersSearch] = useDebouncedValue(customersSearch, 250);

  const user = useAuthUser();

  const { data } = GraphQL.useAdminListCustomersQuery(
    { input: debouncedCustomersSearch },
    { enabled: user.is.SUPER_ADMIN }
  );
  const customers = React.useMemo(() => data?.adminListCustomers, [data]);

  const {
    mutate: createDashboardUser,
    reset,
    ...mutation
  } = GraphQL.useCreateDashboardUserMutation<{ message: string }>();

  const form = useForm<GraphQL.CreateDashboardUserMutationVariables["input"]>({
    validateInputOnBlur: true,
    initialValues: {
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      customerPublicId: !user.is.SUPER_ADMIN ? user.id : "",
      role: GraphQL.DashboardUserRole.User,
    },
    validate: {
      firstName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid first name"),
      lastName: (value: string) => (value.trim().length >= 2 ? null : "Please enter a valid last name"),
      email: (value: string | undefined) =>
        validateEmail(value) === null ? null : "Please enter a valid email address",
      username: (value: string) =>
        value?.length >= 2 && value.length <= 32 ? null : "Please choose a username between 2 and 32 characters",
      customerPublicId: (value: string) => (user.is.SUPER_ADMIN && !value ? "Please select a customer" : null),
    },
  });

  React.useEffect(() => reset, []);

  const handleSubmit = React.useCallback(
    (input: typeof form.values) => {
      createDashboardUser({ input }, { onSuccess });
    },
    [createDashboardUser, onSuccess]
  );

  const overlay = React.useMemo(
    () =>
      createDrawerOverlay({
        loading: {
          message: "Creating user...",
          description: "Please wait, do not close this screen.",
        },
        error: {
          message: mutation.error?.message || "Failed to create user...",
          description: "Please try again later.",
        },
        success: {
          message: "User created!",
        },
      })(mutation),
    [mutation]
  );

  const customersSelectData = React.useMemo(
    () => (customers || []).map((customer) => ({ label: customer.publicId, value: customer.publicId })),
    [customers]
  );

  const roleSelectData = React.useMemo(() => dashboardUserRoleOptions(user.is.SUPER_ADMIN), [user.is.SUPER_ADMIN]);

  if (overlay) return <RpaDrawerOverlay overlay={false} {...overlay} />;

  return (
    <form id={FORM_ID} onSubmit={form.onSubmit(handleSubmit)}>
      <RpaDrawer.Body>
        {user.is.SUPER_ADMIN && (
          <RpaDropdown
            label="Customer"
            placeholder="Select.."
            data={customersSelectData}
            searchable
            onSearchChange={setCustomersSearch}
            {...form.getInputProps("customerPublicId")}
          />
        )}

        <RpaTextInput label="First Name" placeholder="Enter" {...form.getInputProps("firstName")} />
        <RpaTextInput label="Last Name" placeholder="Enter" {...form.getInputProps("lastName")} />
        <RpaTextInput label="Email" placeholder="Enter" {...form.getInputProps("email")} />
        <RpaTextInput label="Username" placeholder="Enter" {...form.getInputProps("username")} />
        <RpaDropdown label="Role" placeholder="Select.." data={roleSelectData} {...form.getInputProps("role")} />
      </RpaDrawer.Body>
      <RpaDrawer.Footer>
        <RpaDrawer.FooterActions>
          <Button form={FORM_ID} type="submit" variant="filled" intent="primary" size="large" fullWidth>
            Send Invitation
          </Button>
          <RpaButtonLink onClick={onCancel}>Cancel</RpaButtonLink>
        </RpaDrawer.FooterActions>
      </RpaDrawer.Footer>
    </form>
  );
};
